// Rahmen der API-Schicht: Client, Abfrage-Cache, Sitzung und die Manifeste der Oberflächen-Module.
// Die Anwendung setzt <ApiProvider> einmal (web/App.tsx). Tests und der Schaukasten reichen einen eigenen Client,
// eine feste Sitzung und eigene Manifeste herein – so laufen die Seitengerüste ohne Server.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createContext, use, useMemo, type ReactNode } from 'react'
import { isAppError } from '@shared/errors'
import type { WebModule } from '../module'
import { webModules } from '../registry.gen'
import { getApiClient, isUnreachableError, type ApiClient } from './client'
import type { SessionPayload } from './types'

const ClientContext = createContext<ApiClient | null>(null)
const SessionOverrideContext = createContext<SessionPayload | null | undefined>(undefined)
const ModulesContext = createContext<readonly WebModule[]>(webModules)

/** Der Client dieser Umgebung: der hereingereichte (Tests, Schaukasten) oder der der Anwendung. */
export const useApiClient = (): ApiClient => use(ClientContext) ?? getApiClient()

/** undefined = keine feste Sitzung, es gilt der Sitzungs-Store */
export const useSessionOverride = (): SessionPayload | null | undefined => use(SessionOverrideContext)

/** Manifeste aller Oberflächen-Module (Navigation, Befehle, Verknüpfungen, Scan-Ziele). */
export const useWebModules = (): readonly WebModule[] => use(ModulesContext)

/**
 * Abfrage-Cache mit den Regeln der Oberfläche: Daten gelten 15 s als frisch (Echtzeit-Hinweise und Änderungen
 * laden gezielt neu), wiederholt wird nur, was sich lohnt (Verbindung weg, Datensatz kurz gesperrt) – ein
 * „Recht fehlt“ oder „nicht gefunden“ wird durch Wiederholen nicht besser.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) =>
          failureCount < 2 && isAppError(error) && (error.retryable || isUnreachableError(error)),
        retryDelay: (attempt) => Math.min(400 * 2 ** attempt, 4000),
      },
      mutations: { retry: false },
    },
  })
}

export interface ApiProviderProps {
  queryClient: QueryClient
  /** Standard: der Client der Anwendung */
  client?: ApiClient
  /** feste Sitzung statt des Sitzungs-Stores (Tests, Schaukasten); null = nicht angemeldet */
  session?: SessionPayload | null
  /** Standard: web/registry.gen.ts */
  modules?: readonly WebModule[]
  children: ReactNode
}

export function ApiProvider({ queryClient, client, session, modules, children }: ApiProviderProps) {
  let tree = <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  if (modules) tree = <ModulesContext value={modules}>{tree}</ModulesContext>
  if (session !== undefined) tree = <SessionOverrideContext value={session}>{tree}</SessionOverrideContext>
  if (client) tree = <ClientContext value={client}>{tree}</ClientContext>
  return tree
}

/** Route der Hülle, die zu einer Belegkennung die Belegart nachschlägt und zur Seite ihres Moduls leitet. */
export const documentRoute = (id: string): string => `/documents/${encodeURIComponent(id)}`

/**
 * Verknüpfungen zwischen Datensätzen aus allen Manifesten: Art des Datensatzes → Pfad.
 *
 * Schlüssel mit Unterart (`document:goods_receipt`, `<art>:<unterart>`) gehen vor; fehlt einer, gilt der Schlüssel
 * der Art. Belege gehören mehreren Modulen: Jedes meldet `document:<belegart>` an; für `document` ohne Belegart
 * (Aufgaben, Benachrichtigungen, Journal, Audit) und für Belegarten ohne eigene Seite antwortet die Hülle mit
 * `/documents/:id`, das die Belegart nachschlägt.
 */
export function buildEntityLinks(modules: readonly WebModule[]): (type: string, id: string) => string | null {
  const links = new Map<string, (id: string) => string>()
  for (const module of modules) for (const [type, to] of Object.entries(module.entityLinks)) links.set(type, to)
  return (type, id) => {
    const base = type.split(':', 1)[0] ?? type
    const to = links.get(type) ?? links.get(base)
    if (to) return to(id)
    return base === 'document' ? documentRoute(id) : null
  }
}

export function useEntityLinks(): (type: string, id: string) => string | null {
  const modules = useWebModules()
  return useMemo(() => buildEntityLinks(modules), [modules])
}
